import React from 'react';

import {assert} from 'Asserts';
import ChooseImportIntoRegistryModalPane from 'bulk-import/panes/ChooseImportIntoRegistryModalPane';
import ChooseImportLocationModalPane from 'bulk-import/panes/ChooseImportLocationModalPane';
import ChooseImportTrialSamplesModalPane from 'bulk-import/panes/ChooseImportTrialSamplesModalPane';
import ImportInstrumentDataModalPane from 'bulk-import/panes/ImportInstrumentDataModalPane';
import MapColumnNamesModalPane from 'bulk-import/panes/MapColumnNamesModalPane';
import MapDerivedColumnsModalPane from 'bulk-import/panes/MapDerivedColumnsModalPane';
import ShowErrorsModalPane from 'bulk-import/panes/ShowErrorsModalPane';
import StorableImportSettingsModalPane from 'bulk-import/panes/StorableImportSettingsModalPane';
import UploadSpreadsheetModalPane from 'bulk-import/panes/UploadSpreadsheetModalPane';
import {PaneKey} from 'bulk-import/SpreadsheetBulkImportConstants';
import {SpreadsheetBulkImportState} from 'bulk-import/SpreadsheetBulkImportStore';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import UserConstants from 'constants/UserConstants';
import DatasetConfigModal from 'lab-automation/settings/automation-output-file-config/DatasetConfigModal';
import SetColumnsMapModal from 'lab-automation/settings/automation-output-file-config/SetColumnsMapModal';
import {getComplexPolymerSchemaConfiguration} from 'modbio/complex-polymers/ComplexPolymerSchemaUtil';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import preferences from 'Preferences';

const {LANGUAGE_PREFERENCE_KEY, SPREADSHEET_IMPORT_LANGUAGE_TAG} = UserConstants;

export interface SpreadsheetBulkImportModalProps extends SpreadsheetBulkImportState {
  onRequestClose: () => void;
  footer?: React.ReactNode;
  importFromSpreadsheet: () => void;
  importItemWord: string;
  title: string;
  isSchemaRequired?: boolean;
  complexPolymerSchemaConfigurations?: Array<ComplexPolymerSchemaConfigGraphQL>;
  maxAllowedRowsToImport?: number;
  inferDataTypes?: boolean;
}

interface SharedModalProps {
  closeableOnBackgroundClick: false;
  closeableOnEscapeKey: false;
  enableRedesign: true;
  title: string;
  footer: React.ReactNode;
  onRequestClose: () => void;
}

class SpreadsheetBulkImportModal extends React.Component<SpreadsheetBulkImportModalProps> {
  static defaultProps = {
    isSchemaRequired: true,
  };

  private getSharedModalProps = (): SharedModalProps => ({
    closeableOnBackgroundClick: false,
    closeableOnEscapeKey: false,
    enableRedesign: true,
    title: this.props.title,
    footer: this.props.footer,
    onRequestClose: this.props.onRequestClose,
  });

  render(): JSX.Element | null {
    const paneIndex = this.props.panes.findIndex(({paneKey}) => paneKey === this.props.paneKey);
    const pane = this.props.panes[paneIndex];
    switch (pane.paneKey) {
      case PaneKey.SET_IMPORT_SETTINGS_FOR_DESTINATION: {
        const {
          getEnabledOptions,
          showRegistries,
          showInventory,
          shouldShowSchemaSelector,
          onDestinationFolderChange,
          folderSelectorLabel,
          folderSelectorPopoverContent,
          shouldFilterToComplexPolymerSchemas,
        } = pane.configuration;
        return (
          <ChooseImportLocationModalPane
            getEnabledOptions={getEnabledOptions}
            showRegistries={showRegistries}
            showInventory={showInventory}
            shouldShowSchemaSelector={shouldShowSchemaSelector}
            selectedDestination={this.props.selectedDestination}
            isLoading={false}
            modalProps={this.getSharedModalProps()}
            options={this.props.options}
            selectedSchemaIds={this.props.selectedSchemaIds}
            onDestinationFolderChange={onDestinationFolderChange}
            schemalessCreationEntityType={this.props.schemalessCreationEntityType}
            isSchemaRequired={this.props.isSchemaRequired}
            folderSelectorLabel={folderSelectorLabel}
            folderSelectorPopoverContent={folderSelectorPopoverContent}
            complexPolymerSchemaConfigurations={this.props.complexPolymerSchemaConfigurations}
            shouldFilterToComplexPolymerSchemas={shouldFilterToComplexPolymerSchemas}
          />
        );
      }
      case PaneKey.SET_IMPORT_SETTINGS_FOR_REGISTRY: {
        return (
          <ChooseImportIntoRegistryModalPane
            selectedSchemaIds={this.props.selectedSchemaIds}
            {...this.props.registryImportSettings}
            selectedDestination={this.props.selectedDestination}
            modalProps={this.getSharedModalProps()}
            options={this.props.options}
          />
        );
      }
      case PaneKey.SET_TRIAL_SAMPLES_IMPORT_SETTINGS: {
        return (
          <ChooseImportTrialSamplesModalPane
            selectedSchemaIds={this.props.selectedSchemaIds}
            {...this.props.registryImportSettings}
            selectedDestination={this.props.selectedDestination}
            modalProps={this.getSharedModalProps()}
            options={this.props.options}
          />
        );
      }
      case PaneKey.SET_STORABLE_IMPORT_SETTINGS: {
        return (
          <StorableImportSettingsModalPane
            {...pane.configuration}
            modalProps={this.getSharedModalProps()}
            {...this.props.registryImportSettings}
            selectedSchemaIds={this.props.selectedSchemaIds}
          />
        );
      }
      case PaneKey.UPLOAD_SPREADSHEET: {
        return (
          <UploadSpreadsheetModalPane
            getExampleImageURL={pane.configuration.getExampleImageURL}
            onUploadSuccessAnalyticsCallback={pane.configuration.onUploadSuccessAnalyticsCallback}
            onUploadFailAnalyticsCallback={pane.configuration.onUploadFailAnalyticsCallback}
            errorMessage={this.props.fileData && this.props.fileData.errorMessage}
            file={this.props.file}
            importEntityType={SpreadsheetBulkImportUtil.getImportEntityType(
              this.props.selectedSchemaIds,
              this.props.schemalessCreationEntityType
            )}
            importItemWord={this.props.importItemWord}
            importSourceType={this.props.importSourceType}
            importType={this.props.importType!}
            isFirstPane={paneIndex === 0 || pane.configuration.hideBackButton}
            languageTag={
              preferences.get(SPREADSHEET_IMPORT_LANGUAGE_TAG) || preferences.get(LANGUAGE_PREFERENCE_KEY)
            }
            modalProps={this.getSharedModalProps()}
            options={this.props.options}
            rawText={this.props.rawText}
            selectedSchemaIds={this.props.selectedSchemaIds}
            uploadState={this.props.fileData && this.props.fileData.state}
            maxAllowedRowsToImport={this.props.maxAllowedRowsToImport}
            inferDataTypes={this.props.inferDataTypes}
          />
        );
      }
      case PaneKey.MAP_COLUMN_NAMES: {
        return (
          <MapColumnNamesModalPane
            customHandleGoToNextPaneFn={pane.configuration.customHandleGoToNextPaneFn}
            getValidColumnTypes={pane.configuration.getValidColumnTypes}
            getSchemaFieldsToHideByColumnType={pane.configuration.getSchemaFieldsToHideByColumnType}
            getRequiredColumnsError={pane.configuration.getRequiredColumnsError}
            onValidateSpreadsheet={pane.configuration.onValidateSpreadsheet}
            getValidSchemaFieldIds={pane.configuration.getValidSchemaFieldIds}
            shouldResolveLinkColumns={pane.configuration.shouldResolveLinkColumns}
            linkItemsFolderSelectorLabel={pane.configuration.linkItemsFolderSelectorLabel}
            linkItemsFolderSelectorDescription={pane.configuration.linkItemsFolderSelectorDescription}
            schemalessCreationEntityType={this.props.schemalessCreationEntityType ?? undefined}
            fileData={this.props.fileData!}
            importType={this.props.importType!}
            linkFolderIds={this.props.linkFolderIds}
            modalProps={this.getSharedModalProps()}
            options={this.props.options}
            registryImportSettings={this.props.registryImportSettings}
            selectedSchemaIds={this.props.selectedSchemaIds}
            tableColumnNames={this.props.tableColumnNames}
            dataTableKeyFields={this.props.dataTableKeyFields}
            _timeLimit={this.props._timeLimit}
            savedMappingsConfig={pane.configuration.savedMappingsConfig}
            {...(this.props.complexPolymerSchemaConfigurations && {
              complexPolymerSchemaConfiguration: getComplexPolymerSchemaConfiguration(
                this.props.selectedSchemaIds.tagSchemaId ?? null,
                this.props.complexPolymerSchemaConfigurations
              ),
            })}
            selectedDestination={this.props.selectedDestination}
          />
        );
      }
      case PaneKey.MAP_DERIVED_COLUMNS: {
        return (
          <MapDerivedColumnsModalPane
            columnTransformConfigs={pane.configuration.columnTransformConfigs}
            derivedColumnsData={this.props.derivedColumnsData}
            derivedColumnsKey={pane.configuration.derivedColumnsKey}
            fileData={this.props.fileData!}
            importType={this.props.importType!}
            linkFolderIds={this.props.linkFolderIds}
            modalProps={this.getSharedModalProps()}
            onUpdateDerivedColumns={pane.configuration.onUpdateDerivedColumns}
            onValidateSpreadsheet={pane.configuration.onValidateSpreadsheet}
            schemalessCreationEntityType={this.props.schemalessCreationEntityType!}
            selectedSchemaIds={this.props.selectedSchemaIds}
            tableColumnNames={this.props.tableColumnNames}
          />
        );
      }
      case PaneKey.MAP_OUTPUT_FILE_COLUMNS: {
        return (
          <SetColumnsMapModal
            {...pane.configuration}
            modalProps={this.getSharedModalProps()}
            uploadedFileData={this.props.fileData}
          />
        );
      }
      case PaneKey.INSTRUMENT_DATA: {
        return (
          <ImportInstrumentDataModalPane
            {...pane.configuration}
            modalProps={this.getSharedModalProps()}
            inferDataTypes={this.props.inferDataTypes}
          />
        );
      }
      case PaneKey.DATASET_CONFIG: {
        return (
          <DatasetConfigModal
            {...pane.configuration}
            modalProps={this.getSharedModalProps()}
            uploadedFileData={this.props.fileData}
          />
        );
      }
      case PaneKey.SHOW_ERRORS: {
        return (
          <ShowErrorsModalPane
            customHandleGoToPrevPaneFn={pane.configuration.customHandleGoToPrevPaneFn}
            derivedColumnsData={this.props.derivedColumnsData}
            enableReplaceLinkWithText={pane.configuration.enableReplaceLinkWithText}
            enableSkipErrors={pane.configuration.enableSkipErrors}
            errorData={this.props.errorData}
            fileDataColumns={this.props.fileData!.columns}
            importType={this.props.importType!}
            isLastPane={paneIndex === this.props.panes.length - 1}
            modalProps={this.getSharedModalProps()}
            options={this.props.options}
            onImport={this.props.importFromSpreadsheet}
            selectedSchemaIds={this.props.selectedSchemaIds}
            uploadFlow={pane.configuration.uploadFlow}
          />
        );
      }
      default:
        assert(false, 'Unrecognized pane state!');
        return null;
    }
  }
}

export default SpreadsheetBulkImportModal;
